import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { FileText, Home, LogOut, Zap, TrendingUp, Users, BarChart3 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export const Navigation = ({ isSharedView = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  
  const isActive = (path) => location.pathname === path;
  
  const handleLogout = () => {
    logout();
    toast.success('Déconnexion réussie');
    navigate('/login');
  };
  
  // En mode partagé (client), afficher une navigation minimale
  if (isSharedView && !user) {
    return (
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center h-14">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="/logo-pde.png" 
                alt="Place des Énergies" 
                className="h-8 w-auto"
              />
            </Link>
          </div>
        </div>
      </nav>
    );
  }
  
  return (
    <nav className="sticky top-0 z-50 glass-effect border-b">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3 group">
            <img 
              src="/logo-pde.png" 
              alt="Place des Énergies" 
              className="h-10 w-auto group-hover:scale-105 transition-transform duration-300"
            />
          </Link>
          
          <div className="flex items-center gap-2">
            <Link to="/">
              <Button 
                variant={isActive("/") ? "default" : "ghost"}
                size="sm"
                className={cn(
                  "gap-2",
                  isActive("/") && "shadow-md"
                )}
              >
                <Home className="w-4 h-4" />
                <span className="hidden sm:inline">Accueil</span>
              </Button>
            </Link>
            
            <Link to="/reports">
              <Button 
                variant={isActive("/reports") ? "default" : "ghost"}
                size="sm"
                className={cn(
                  "gap-2",
                  isActive("/reports") && "shadow-md"
                )}
              >
                <FileText className="w-4 h-4" />
                <span className="hidden sm:inline">TICFE</span>
              </Button>
            </Link>
            
            <Link to="/optimization/reports">
              <Button 
                variant={isActive("/optimization/reports") || location.pathname.startsWith("/optimization") ? "default" : "ghost"}
                size="sm"
                className={cn(
                  "gap-2",
                  (isActive("/optimization/reports") || location.pathname.startsWith("/optimization")) && "shadow-md"
                )}
              >
                <Zap className="w-4 h-4" />
                <span className="hidden sm:inline">Optimisation</span>
              </Button>
            </Link>
            
            <Link to="/dashboard">
              <Button 
                variant={location.pathname.startsWith("/consumption") || isActive("/dashboard") ? "default" : "ghost"}
                size="sm"
                className={cn(
                  "gap-2",
                  (location.pathname.startsWith("/consumption") || isActive("/dashboard")) && "shadow-md"
                )}
              >
                <TrendingUp className="w-4 h-4" />
                <span className="hidden sm:inline">Consommation</span>
              </Button>
            </Link>
            
            {/* Admin link - only for admin and franchise_owner */}
            {user && (user.role === 'admin' || user.role === 'franchise_owner') && (
              <Link to="/admin/users">
                <Button 
                  variant={isActive("/admin/users") ? "default" : "ghost"}
                  size="sm"
                  className={cn(
                    "gap-2",
                    isActive("/admin/users") && "shadow-md"
                  )}
                >
                  <Users className="w-4 h-4" />
                  <span className="hidden sm:inline">
                    {user.role === 'admin' ? 'Franchises' : 'Équipe'}
                  </span>
                </Button>
              </Link>
            )}
            
            {user && (
              <Button 
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="gap-2"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Déconnexion</span>
              </Button>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
